import {
  Box,
  Button,
  ButtonGroup,
  Card,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { EmptyState } from '../../../components/EmptyState';
import { useStudioAuthenticatedContext } from '../../../context/StudioContext';
import { TracksChart } from './TracksChart';
import * as styles from './TracksTable.styles';

export interface TrackData {
  title: string;
  artist: string;
  plays: number;
  adds: number;
  shares: number;
}

type SortOrder = 'asc' | 'desc';
type ViewMode = 'table' | 'chart';

const ITEMS_PER_PAGE = 20;

export interface TracksTableProps {
  timePeriod: string;
  onTimePeriodChange: (event: SelectChangeEvent<string>) => void;
}

export const TracksTable: React.FC<TracksTableProps> = ({
  timePeriod,
  onTimePeriodChange,
}) => {
  const { backendClient, selectedAppSet } = useStudioAuthenticatedContext();
  const [viewMode, setViewMode] = useState<ViewMode>('chart');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [sortBy, setSortBy] = useState<'title' | 'plays' | 'adds' | 'shares'>(
    'plays',
  );
  const [page, setPage] = useState(1);
  const [tracksData, setTracksData] = useState<TrackData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const scrollPositionRef = useRef<number>(0);
  const prevTimePeriodRef = useRef<string>(timePeriod);

  const handleSort = (column: 'title' | 'plays' | 'adds' | 'shares') => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
    setPage(1);
  };

  // Save scroll position before timePeriod changes
  useEffect(() => {
    if (prevTimePeriodRef.current !== timePeriod) {
      scrollPositionRef.current = window.scrollY;
      prevTimePeriodRef.current = timePeriod;
    }
  });

  // Fetch data from API
  useEffect(() => {
    const fetchData = async () => {
      if (!selectedAppSet) {
        setError('No application selected');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await backendClient.getTrackEngagements({
          appId: selectedAppSet.productionApp.id,
          timePeriod: timePeriod as 'current-month' | 'last-3-months' | 'last-year',
        });

        // Defensive check: ensure response.data exists and is an array
        if (!response || !response.data || !Array.isArray(response.data)) {
          console.error('Invalid response structure:', response);
          setError('Invalid response format from server');
          setTracksData([]);
          return;
        }

        const mappedData: TrackData[] = response.data.map(
          (item: {
            track_title: string;
            total_plays: number;
            total_adds: number;
            total_shares: number;
          }) => {
            // Parse track_title - it might be in "Title – Artist" format or just "Title"
            const titleParts = item.track_title.split(' – ');
            const title = titleParts[0] || item.track_title;
            const artist = titleParts[1] || '';

            return {
              title,
              artist,
              plays: item.total_plays,
              adds: item.total_adds,
              shares: item.total_shares,
            };
          },
        );

        setTracksData(mappedData);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to fetch analytics data',
        );
        setTracksData([]);
      } finally {
        setLoading(false);
        // Restore scroll position after data loads
        requestAnimationFrame(() => {
          window.scrollTo(0, scrollPositionRef.current);
        });
      }
    };

    fetchData();
  }, [timePeriod, backendClient, selectedAppSet]);

  const sortedData = useMemo(() => {
    const data = [...tracksData];
    return data.sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'title':
          comparison = a.title.localeCompare(b.title);
          break;
        case 'plays':
          comparison = a.plays - b.plays;
          break;
        case 'adds':
          comparison = a.adds - b.adds;
          break;
        case 'shares':
          comparison = a.shares - b.shares;
          break;
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });
  }, [tracksData, sortBy, sortOrder]);

  const paginatedData = useMemo(() => {
    const startIndex = (page - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return sortedData.slice(startIndex, endIndex);
  }, [sortedData, page]);

  const totalPages = Math.ceil(sortedData.length / ITEMS_PER_PAGE);

  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const formatNumber = (num: number): string => {
    return num.toLocaleString();
  };

  return (
    <Box sx={styles.tableContainer}>
      <Card elevation={0} sx={styles.cardStyles}>
        {/* Header with title, view toggle, and time period selector */}
        <Box sx={styles.headerContainer}>
          <Box sx={styles.titleContainer}>
            <Typography
              variant="h6"
              color="text.primary"
              sx={styles.titleTypography}
            >
              Tracks
            </Typography>
            {viewMode === 'chart' && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={styles.subtitleTypography}
              >
                Top 10 Tracks
              </Typography>
            )}
          </Box>

          <Box sx={styles.controlsContainer}>
            {/* View Toggle Buttons */}
            <ButtonGroup
              variant="outlined"
              size="small"
              sx={styles.viewToggleGroup}
            >
              <Button
                onClick={() => setViewMode('chart')}
                variant={viewMode === 'chart' ? 'contained' : 'outlined'}
              >
                Chart
              </Button>
              <Button
                onClick={() => setViewMode('table')}
                variant={viewMode === 'table' ? 'contained' : 'outlined'}
              >
                Table
              </Button>
            </ButtonGroup>

            {/* Time Period Selector */}
            <FormControl size="small" sx={styles.formControl}>
              <InputLabel>Time Period</InputLabel>
              <Select
                value={timePeriod}
                label="Time Period"
                onChange={onTimePeriodChange}
              >
                <MenuItem value="current-month">Current month</MenuItem>
                <MenuItem value="last-3-months">Last 3 months</MenuItem>
                <MenuItem value="last-year">Last year</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* Chart or Table Content */}
        {viewMode === 'chart' ? (
          <TracksChart
            data={sortedData.slice(0, 10)}
            loading={loading}
            error={error}
          />
        ) : !loading && !error && sortedData.length === 0 ? (
          <EmptyState message="Data coming soon..." />
        ) : (
          <TableContainer>
          <Table sx={styles.table}>
            <TableHead>
              <TableRow>
                <TableCell sx={styles.nameColumn}>
                  <TableSortLabel
                    active={sortBy === 'title'}
                    direction={sortBy === 'title' ? sortOrder : 'asc'}
                    onClick={() => handleSort('title')}
                    sx={styles.tableSortLabel}
                  >
                    Tracks
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={styles.numericColumn}>
                  <TableSortLabel
                    active={sortBy === 'plays'}
                    direction={sortBy === 'plays' ? sortOrder : 'asc'}
                    onClick={() => handleSort('plays')}
                    sx={styles.tableSortLabel}
                  >
                    Plays
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={styles.numericColumn}>
                  <TableSortLabel
                    active={sortBy === 'adds'}
                    direction={sortBy === 'adds' ? sortOrder : 'asc'}
                    onClick={() => handleSort('adds')}
                    sx={styles.tableSortLabel}
                  >
                    Adds
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={styles.numericColumn}>
                  <TableSortLabel
                    active={sortBy === 'shares'}
                    direction={sortBy === 'shares' ? sortOrder : 'asc'}
                    onClick={() => handleSort('shares')}
                    sx={styles.tableSortLabel}
                  >
                    Shares
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={styles.loadingErrorCell}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={styles.loadingErrorCell}>
                    <Typography color="error">{error}</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedData.map((row, index) => (
                  <TableRow key={`${row.title}-${row.artist}-${index}`}>
                    <TableCell sx={styles.nameColumn}>
                      {row.artist ? `${row.title} – ${row.artist}` : row.title}
                    </TableCell>
                    <TableCell sx={styles.numericColumn}>
                      {formatNumber(row.plays)}
                    </TableCell>
                    <TableCell sx={styles.numericColumn}>
                      {formatNumber(row.adds)}
                    </TableCell>
                    <TableCell sx={styles.numericColumn}>
                      {formatNumber(row.shares)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        )}

        {/* Pagination - Only show in table view */}
        {viewMode === 'table' && totalPages > 1 && (
          <Box sx={styles.paginationContainer}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
              sx={styles.pagination}
            />
          </Box>
        )}
      </Card>
    </Box>
  );
};

